import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Save, Trash2, ShieldCheck, ExternalLink, KeyRound, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, UserPlus, RefreshCw, X, Clipboard, Rocket, Globe, Sparkles } from 'lucide-react'
import {
  api,
  BASE_URL,
  apiHostnameForManifest,
  dashboardOrigin,
  teamsManifestAppId,
} from '../lib/api'
import type { Invite } from '../lib/api'
import { formatTimezoneLabel } from '../lib/timezoneDisplay'
import { useAuthStore } from '../store/authStore'
import { toast } from '../store/toastStore'
import { useNavigate } from 'react-router-dom'
import { NUDGE_CATEGORIES } from '@duva/shared'

const inputClass =
  'w-full bg-warm-inset border border-warm-border rounded-xl px-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal transition-colors'
const sectionClass = 'bg-warm-card border border-warm-border rounded-2xl p-6 mb-5 shadow-card'

interface OrgSettings {
  id: string
  name: string
  plan: string
  employee_count: number
  slack_workspace_id: string | null
  teams_tenant_id: string | null
  signup_country?: string | null
  industry?: string | null
}

interface SsoStatus {
  configured: boolean
  provider: string | null
  domain: string | null
}

export function SettingsPage() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'admin'

  const [org, setOrg] = useState<OrgSettings | null>(null)
  const [orgName, setOrgName] = useState('')
  const [industry, setIndustry] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [erasing, setErasing] = useState(false)
  const [confirmErase, setConfirmErase] = useState(false)

  const [invites, setInvites] = useState<Invite[]>([])
  const [customNudges, setCustomNudges] = useState<any[]>([])
  const [nudgeTitle, setNudgeTitle] = useState('')
  const [nudgeBody, setNudgeBody] = useState('')
  const [nudgeCategory, setNudgeCategory] = useState('leadership')
  const [submittingNudge, setSubmittingNudge] = useState(false)
  const [inviteEmails, setInviteEmails] = useState('')
  const [inviteRole, setInviteRole] = useState('employee')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteResults, setInviteResults] = useState<{ email: string; status: string }[] | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<{ newMembers: number; processed?: number; totalMembers: number } | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)

  const [ssoStatus, setSsoStatus] = useState<SsoStatus | null>(null)
  const [ssoOpen, setSsoOpen] = useState(false)
  const [ssoForm, setSsoForm] = useState({
    provider: 'okta',
    issuer_url: '',
    client_id: '',
    client_secret: '',
    domain: '',
  })
  const [ssoSaving, setSsoSaving] = useState(false)
  const [ssoSaved, setSsoSaved] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [copyStatus, setCopyStatus] = useState(false)
  const [showTeamsManifest, setShowTeamsManifest] = useState(false)

  const [meTimezone, setMeTimezone] = useState('')
  /** Cloudflare edge guess for this browser session (GET /api/me). */
  const [detectedTimezone, setDetectedTimezone] = useState<string | null>(null)
  const [manualIanaOpen, setManualIanaOpen] = useState(false)
  const [tzSaving, setTzSaving] = useState(false)
  const [tzSaved, setTzSaved] = useState(false)
  const [tzError, setTzError] = useState<string | null>(null)

  const [deliveryThreshold, setDeliveryThreshold] = useState(10)
  const [savingThreshold, setSavingThreshold] = useState(false)

  const [featuresPlan, setFeaturesPlan] = useState<string | null>(null)
  const [featuresAvailable, setFeaturesAvailable] = useState<string[]>([])
  const [featuresDisabled, setFeaturesDisabled] = useState<string[]>([])
  const [featuresSavingKey, setFeaturesSavingKey] = useState<string | null>(null)

  const [launchMessage, setLaunchMessage] = useState(
    `Hi team! We're launching Duva 🕊, a light-touch coach that sends science-backed nudges to help us build better work habits. You'll get a short message in Slack, Microsoft Teams, or email about once or twice a week with a small, actionable tip. It takes less than 3 minutes — use Done / Remind me later (or the email link) when you've tried it. Looking forward to growing together!`
  )

  const teamsManifestJson = useMemo(() => {
    const appId = teamsManifestAppId()
    const apiHost = apiHostnameForManifest()
    const validDomains = Array.from(new Set(['www.getduva.com', apiHost])).sort()
    const obj = {
      $schema: 'https://developer.microsoft.com/json-schemas/teams/v1.16/MicrosoftTeams.schema.json',
      manifestVersion: '1.16',
      version: '1.0.0',
      id: appId,
      packageName: 'com.getduva.app',
      name: { short: 'Duva', full: 'Duva — Behavioral Nudge Engine' },
      developer: {
        name: 'Duva',
        websiteUrl: 'https://www.getduva.com',
        privacyUrl: 'https://www.getduva.com/privacy',
        termsOfUseUrl: 'https://www.getduva.com/terms',
      },
      bots: [{ botId: appId, scopes: ['personal', 'team'] }],
      validDomains,
    }
    return JSON.stringify(obj, null, 2)
  }, [])

  useEffect(() => {
    api
      .getMe()
      .then((me) => {
        setMeTimezone(me.timezone ?? '')
        const d = me.detected_timezone?.trim()
        setDetectedTimezone(d && d.length > 0 ? d : null)
      })
      .catch(() => { /* non-critical */ })

    api.getOrgSettings()
      .then(r => {
        const s = r as OrgSettings
        setOrg(s)
        setOrgName(s.name)
        setIndustry(s.industry ?? '')
      })

    api.getSsoStatus()
      .then(r => setSsoStatus(r as SsoStatus))
      .catch(() => { /* non-critical */ })

    if (isAdmin) {
      api.getInvites()
        .then(r => setInvites(r.invites))
        .catch(() => { /* non-critical */ })
      api.getDeliveryThreshold()
        .then((r: { threshold: number | null }) => {
          if (r.threshold !== null) setDeliveryThreshold(r.threshold)
        })
        .catch(() => { /* non-critical */ })
      api.getCustomNudges()
        .then(r => setCustomNudges(r.nudges))
        .catch(() => { /* non-critical */ })
      api.getOrgFeatures()
        .then(r => {
          setFeaturesPlan(r.plan)
          setFeaturesAvailable(r.available)
          setFeaturesDisabled(r.disabled)
        })
        .catch(() => { /* non-critical */ })
    }
  }, [isAdmin])

  const FEATURE_META: Record<string, { label: string; description: string }> = {
    monthly_pulse: {
      label: 'Monthly Leadership Pulse',
      description: 'A 1-minute anonymous DM each month so people can rate org leadership.',
    },
    ama: {
      label: 'Anonymous feedback & AMA',
      description: 'The /duva-feedback and /ama Slack commands; aggregated into themes for admins.',
    },
    shoutouts: {
      label: 'Peer shout-outs',
      description: 'The /duva-shoutout Slack command for cross-team recognition.',
    },
    duva_circles: {
      label: 'Duva Circles',
      description: 'Manager feedback collected from direct reports.',
    },
  }

  async function toggleFeature(key: string, nextEnabled: boolean) {
    if (!featuresAvailable.includes(key)) return
    const next = nextEnabled
      ? featuresDisabled.filter((f) => f !== key)
      : Array.from(new Set([...featuresDisabled, key]))
    setFeaturesSavingKey(key)
    try {
      const r = await api.updateOrgFeatures(next)
      setFeaturesDisabled(r.disabled)
      toast.success(nextEnabled ? `${FEATURE_META[key]?.label ?? key} enabled` : `${FEATURE_META[key]?.label ?? key} disabled`)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update workspace features')
    } finally {
      setFeaturesSavingKey(null)
    }
  }

  async function handleNudgeSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!nudgeTitle || !nudgeBody) return
    setSubmittingNudge(true)
    try {
      await api.submitCustomNudge({ title: nudgeTitle, body: nudgeBody, category: nudgeCategory })
      toast.success('Nudge submitted for review')
      setNudgeTitle('')
      setNudgeBody('')
      const updated = await api.getCustomNudges()
      setCustomNudges(updated.nudges)
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit nudge')
    } finally {
      setSubmittingNudge(false)
    }
  }

  async function handleSave() {
    setSaving(true)
    try {
      await api.updateOrgSettings({ name: orgName, industry: industry.trim() || null })
      const next = await api.getOrgSettings() as OrgSettings
      setOrg(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      toast.success('Organization settings saved')
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not save settings')
    } finally {
      setSaving(false)
    }
  }

  async function handleInvite() {
    const emails = inviteEmails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean)
    if (!emails.length) return
    setInviteSending(true)
    setInviteResults(null)
    try {
      const res = await api.sendInvites(emails, inviteRole)
      setInviteResults(res.results)
      const failed = res.results.filter((r) => r.status === 'email_failed').length
      if (failed > 0) {
        toast.error(`${failed} invite(s) could not be emailed. Check results below.`)
      } else {
        toast.success(`Sent ${emails.length} invite(s)`)
      }
      if (!res.results.some((r) => r.status === 'email_failed')) {
        setInviteEmails('')
      }
      const updated = await api.getInvites()
      setInvites(updated.invites)
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not send invites')
    } finally {
      setInviteSending(false)
    }
  }

  async function handleRevokeInvite(id: string) {
    await api.revokeInvite(id)
    setInvites(prev => prev.filter(i => i.id !== id))
  }

  async function handleSync() {
    setSyncing(true)
    setSyncResult(null)
    setSyncError(null)
    try {
      const res = await api.syncMembers() as { newMembers: number; processed?: number; totalMembers: number }
      setSyncResult(res)
      const updated = await api.getOrgSettings() as OrgSettings
      setOrg(updated)
      toast.success('Team roster synced from Slack')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Sync failed'
      setSyncError(msg)
      toast.error(msg)
    } finally {
      setSyncing(false)
    }
  }

  async function handleSsoSave() {
    setSsoSaving(true)
    setSsoError(null)
    try {
      await api.configureSso(ssoForm)
      setSsoSaved(true)
      setSsoStatus({ configured: true, provider: ssoForm.provider, domain: ssoForm.domain })
      setTimeout(() => setSsoSaved(false), 3000)
      toast.success('SSO configuration saved')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to save SSO configuration'
      setSsoError(msg)
      toast.error(msg)
    } finally {
      setSsoSaving(false)
    }
  }

  async function handleErase() {
    if (!confirmErase) { setConfirmErase(true); return }
    setErasing(true)
    try {
      await fetch('/api/me', { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('duva_token')}` } })
      logout()
      navigate('/login')
    } finally {
      setErasing(false)
    }
  }

  async function handleUseConnectionTimezone() {
    if (!detectedTimezone) return
    setTzSaving(true)
    setTzError(null)
    setTzSaved(false)
    try {
      await api.patchMe({ timezone: detectedTimezone })
      setMeTimezone(detectedTimezone)
      setTzSaved(true)
      setTimeout(() => setTzSaved(false), 2500)
      toast.success('Using connection timezone (Cloudflare)')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not save timezone'
      setTzError(msg)
      toast.error(msg)
    } finally {
      setTzSaving(false)
    }
  }

  async function handleWorkspaceDefaultTimezone() {
    setTzSaving(true)
    setTzError(null)
    setTzSaved(false)
    try {
      await api.patchMe({ timezone: null })
      setMeTimezone('')
      setTzSaved(true)
      setTimeout(() => setTzSaved(false), 2500)
      toast.success('Using workspace default timezone')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not clear timezone'
      setTzError(msg)
      toast.error(msg)
    } finally {
      setTzSaving(false)
    }
  }

  async function handleSaveTimezone() {
    setTzSaving(true)
    setTzError(null)
    setTzSaved(false)
    try {
      const trimmed = meTimezone.trim()
      await api.patchMe({ timezone: trimmed === '' ? null : trimmed })
      setTzSaved(true)
      setTimeout(() => setTzSaved(false), 2500)
      toast.success('Timezone saved')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not save timezone'
      setTzError(msg)
      toast.error(msg)
    } finally {
      setTzSaving(false)
    }
  }

  async function handleSaveThreshold() {
    setSavingThreshold(true)
    try {
      await api.setDeliveryThreshold(deliveryThreshold)
      toast.success('Alert threshold updated')
    } catch (e: unknown) {
      toast.error('Failed to save threshold')
    } finally {
      setSavingThreshold(false)
    }
  }

  async function handleCopyLaunchMessage() {
    try {
      await navigator.clipboard.writeText(launchMessage)
      setCopyStatus(true)
      setTimeout(() => setCopyStatus(false), 2000)
    } catch (err) {
      console.error('Failed to copy message', err)
    }
  }

  async function handleCopyInviteLink(token: string) {
    const link = `${dashboardOrigin()}/invite/accept?token=${encodeURIComponent(token)}`
    try {
      await navigator.clipboard.writeText(link)
      toast.success('Invite link copied to clipboard')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  const providerLabels: Record<string, string> = {
    okta: 'Okta',
    azure: 'Microsoft Entra ID (Azure AD)',
    google: 'Google Workspace',
    generic: 'Generic OIDC provider',
  }

  const hasSlack = !!org?.slack_workspace_id
  const hasTeams = !!org?.teams_tenant_id

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Settings</h1>
        <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>
          {isAdmin ? 'Manage your organization and account' : 'Manage your account'}
        </p>
      </div>

      {isAdmin && (
        <section className={sectionClass}>
          <h2 className="text-sm font-semibold text-ink mb-4">Organization</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-ink-soft mb-1.5">Organization name</label>
              <input
                type="text"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs text-ink-soft mb-1.5">Industry / category (optional)</label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className={inputClass}
                placeholder="e.g. SaaS, Healthcare, Agency"
              />
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-ink-muted">
              <span>Plan: <span className="text-ink capitalize font-medium">{org?.plan ?? '…'}</span></span>
              <span>Members: <span className="text-ink font-medium">{org?.employee_count ?? '…'}</span></span>
              {org?.signup_country && (
                <span>Region: <span className="text-ink font-medium">{org.signup_country}</span></span>
              )}
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal" style={{background: "#2A7A8A"}}
            >
              <Save size={14} />
              {saved ? 'Saved!' : saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </section>
      )}

      {isAdmin && (
        <>
          <section className={sectionClass}>
          <div className="flex items-center justify-between mb-4 gap-3">
            <div className="flex items-center gap-2">
              <UserPlus size={16} className="text-brand-teal" />
              <h2 className="text-sm font-semibold text-ink">Team Members</h2>
            </div>
            {hasSlack ? (
              <button
                type="button"
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-brand-teal transition-colors disabled:opacity-50 font-medium shrink-0"
              >
                <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing…' : 'Sync from Slack'}
              </button>
            ) : null}
          </div>

          {!hasSlack && (
            <p className="text-xs text-ink-muted mb-4 leading-relaxed">
              {hasTeams
                ? 'This workspace signed in with Microsoft Teams. Add people with email invites below (Microsoft 365 directory sync is not available yet). After someone joins, they should open a chat with the Duva bot in Teams so nudges can be delivered.'
                : 'Connect Slack or Microsoft Teams under Integrations to link your workspace. Until Slack is connected, use email invites to add teammates.'}
            </p>
          )}

          {syncResult && (
            <div className="mb-4 text-xs text-emerald-800 bg-[#eef5ec] border border-[#B8C7B1] rounded-lg px-3 py-2">
              {syncResult.newMembers > 0
                ? `✓ Added ${syncResult.newMembers} new member(s) · ${syncResult.totalMembers} total`
                : (syncResult.processed ?? 0) > 0
                  ? `✓ Refreshed ${syncResult.processed} from Slack · ${syncResult.totalMembers} total`
                  : `✓ Up to date · ${syncResult.totalMembers} total`}
            </div>
          )}

          {syncError && (
            <div className="mb-4 text-xs text-red-800 bg-[#fdf2f2] border border-[#e8cfcf] rounded-lg px-3 py-2">
              {syncError}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-ink-soft mb-1.5">Invite by email</label>
              <textarea
                value={inviteEmails}
                onChange={e => setInviteEmails(e.target.value)}
                placeholder="alice@company.com, bob@company.com&#10;(one per line or comma-separated)"
                rows={3}
                className={`${inputClass} resize-none`}
              />
            </div>
            <div className="flex items-center gap-3">
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                className="bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-xs text-ink focus:outline-none focus:border-brand-teal"
              >
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="button"
                onClick={handleInvite}
                disabled={inviteSending || !inviteEmails.trim()}
                className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal" style={{background: "#2A7A8A"}}
              >
                <UserPlus size={13} />
                {inviteSending ? 'Sending…' : 'Send invites'}
              </button>
            </div>

            {inviteResults && (
              <div className="space-y-1">
                {inviteResults.map(r => (
                  <div
                    key={r.email}
                    className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${
                      r.status === 'sent'
                        ? 'bg-[#eef5ec] text-emerald-900 border-[#B8C7B1]'
                        : r.status === 'email_failed'
                          ? 'bg-[#fdf2f2] text-red-900 border-[#e8cfcf]'
                          : r.status === 'already_member'
                            ? 'bg-warm-inset text-ink-muted border-warm-border'
                            : 'bg-[#fdf8f0] text-amber-900 border-[#e8d4b8]'
                    }`}
                  >
                    {r.status === 'email_failed' ? (
                      <AlertCircle size={12} className="shrink-0" />
                    ) : (
                      <CheckCircle2 size={12} className="shrink-0" />
                    )}
                    <span>{r.email}</span>
                    <span className="ml-auto opacity-80 text-right">
                      {r.status === 'sent'
                        ? 'Invite sent'
                        : r.status === 'email_failed'
                          ? 'Email not sent — try again or check email setup'
                          : r.status === 'already_member'
                            ? 'Already a member'
                            : 'Already invited'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {invites.length > 0 && (
            <div className="mt-5 pt-5 border-t border-warm-border">
              <p className="text-xs font-semibold text-ink-soft mb-3">Pending invites ({invites.length})</p>
              <div className="space-y-2">
                {invites.map(invite => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between bg-warm-inset rounded-lg px-3 py-2 border border-warm-border"
                  >
                    <div>
                      <p className="text-xs text-ink font-medium">{invite.email}</p>
                      <p className="text-xs text-ink-muted mt-0.5">
                        {invite.role} · Invited by {invite.invited_by_name} · Expires {new Date(invite.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleCopyInviteLink(invite.id)}
                        className="text-brand-teal hover:text-brand-teal/80 transition-colors"
                        title="Copy magic link"
                      >
                        <Clipboard size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevokeInvite(invite.id)}
                        className="text-ink-soft hover:text-red-700 transition-colors ml-1"
                        title="Revoke invite"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {featuresAvailable.length > 0 && (
          <section className={sectionClass}>
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-brand-teal" />
                <h2 className="text-sm font-semibold text-ink">Workspace features</h2>
              </div>
              {featuresPlan && (
                <span className="text-xs text-ink-muted capitalize">{featuresPlan} plan</span>
              )}
            </div>
            <p className="text-xs text-ink-muted mb-4 leading-relaxed">
              Features are on by default. Turn one off to pause it for the whole workspace; you can re-enable any time.
            </p>
            <div className="space-y-3">
              {featuresAvailable.map((key) => {
                const meta = FEATURE_META[key] ?? { label: key, description: '' }
                const enabled = !featuresDisabled.includes(key)
                const busy = featuresSavingKey === key
                return (
                  <div
                    key={key}
                    className="flex items-start justify-between gap-4 bg-warm-inset rounded-lg px-3 py-3 border border-warm-border"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-ink">{meta.label}</p>
                      {meta.description && (
                        <p className="text-xs text-ink-muted mt-0.5 leading-relaxed">{meta.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleFeature(key, !enabled)}
                      disabled={busy}
                      aria-pressed={enabled}
                      className={clsx(
                        'shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 border',
                        enabled
                          ? 'bg-[#eef5ec] text-emerald-900 border-[#B8C7B1]'
                          : 'bg-warm-card text-ink-muted border-warm-border'
                      )}
                    >
                      {busy ? '…' : enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        <section className={sectionClass}>
          <div className="flex items-center gap-2 mb-4">
            <Rocket size={16} className="text-brand-teal" />
            <h2 className="text-sm font-semibold text-ink">Launch Duva</h2>
          </div>
          <p className="text-xs text-ink-muted mb-4 leading-relaxed">
            Ready to roll out Duva to your organization? Customize then copy this message to share in Slack, Microsoft Teams, or email.
          </p>
          <div className="space-y-4">
            <textarea
              value={launchMessage}
              onChange={e => setLaunchMessage(e.target.value)}
              className={`${inputClass} resize-none italic`}
              rows={5}
            />
            <button
              onClick={handleCopyLaunchMessage}
              className="flex items-center gap-2 bg-[#2A7A8A] text-white font-medium text-xs px-4 py-2 rounded-lg transition-all hover:bg-[#236b7a] active:scale-95 shadow-glow-teal"
            >
              {copyStatus ? (
                <>
                  <CheckCircle2 size={13} />
                  Copied!
                </>
              ) : (
                <>
                  <Clipboard size={13} />
                  Copy message
                </>
              )}
            </button>
          </div>
        </section>

        <section className={sectionClass}>
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-brand-teal" />
            <h2 className="text-sm font-semibold text-ink">Operations</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-ink-soft mb-1.5">
                Delivery health alert threshold (%)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={deliveryThreshold}
                  onChange={e => setDeliveryThreshold(parseInt(e.target.value) || 0)}
                  className={`${inputClass} w-24`}
                />
                <button
                  type="button"
                  onClick={handleSaveThreshold}
                  disabled={savingThreshold}
                  className="px-4 py-2 rounded-lg bg-warm-card border border-warm-border text-xs font-medium text-ink hover:bg-warm-inset transition-colors"
                >
                  {savingThreshold ? 'Saving…' : 'Update'}
                </button>
              </div>
              <p className="text-[10px] text-ink-soft mt-1.5">
                We'll highlight organizational health as &quot;At Risk&quot; if organizational action rates fall below this number.
              </p>
              {!hasSlack && (
                <p className="text-[10px] text-amber-900/90 mt-3 leading-relaxed bg-[#fdf8f0] border border-[#e8d4b8] rounded-lg px-3 py-2">
                  Weekly &quot;zero deliveries&quot; alerts to admins are sent via <strong className="text-ink">Slack DM</strong> when Slack is
                  connected. Connect Slack under Integrations if you rely on email or Teams only and still want those pings.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-brand-teal" />
            <h2 className="text-sm font-semibold text-ink">Custom Nudge Studio</h2>
          </div>
          <p className="text-xs text-ink-muted mb-4 leading-relaxed">
            Create proprietary nudges for your organization. Once submitted, our team will review them for behavioral efficacy before they are activated.
          </p>

          <form onSubmit={handleNudgeSubmit} className="space-y-4 mb-6">
            <div>
              <label className="block text-[10px] uppercase font-bold text-ink-muted tracking-wider mb-1.5 ml-1">
                Nudge Title
              </label>
              <input
                value={nudgeTitle}
                onChange={e => setNudgeTitle(e.target.value)}
                placeholder="e.g. Share a small win today"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-bold text-ink-muted tracking-wider mb-1.5 ml-1">
                Nudge Content
              </label>
              <textarea
                value={nudgeBody}
                onChange={e => setNudgeBody(e.target.value)}
                placeholder="Write the message that will be sent via Slack, Teams, or email..."
                className={`${inputClass} h-24 resize-none`}
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-[10px] uppercase font-bold text-ink-muted tracking-wider mb-1.5 ml-1">
                  Category
                </label>
                <select
                  value={nudgeCategory}
                  onChange={e => setNudgeCategory(e.target.value)}
                  className={inputClass}
                >
                  {NUDGE_CATEGORIES.map(c => (
                    <option key={c} value={c} className="capitalize">{c}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={submittingNudge || !nudgeTitle || !nudgeBody}
                  className="flex items-center gap-2 bg-[#2A7A8A] text-white font-medium text-xs px-6 py-2.5 rounded-xl transition-all hover:bg-[#236b7a] active:scale-95 shadow-glow-teal disabled:opacity-50"
                >
                  {submittingNudge ? 'Submitting...' : 'Submit for Review'}
                </button>
              </div>
            </div>
          </form>

          {customNudges.length > 0 && (
            <div className="space-y-2 mt-6 pt-6 border-t border-warm-border/50">
              <h3 className="text-[10px] uppercase font-bold text-ink-muted tracking-wider mb-3">Your Custom Nudges</h3>
              {customNudges.map(cn => (
                <div key={cn.id} className="flex items-center justify-between p-3 bg-warm-inset rounded-xl border border-warm-border/40">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-ink truncate">{cn.title}</p>
                    <p className="text-[10px] text-ink-soft capitalize">{cn.category} · {cn.status.replace('_', ' ')}</p>
                  </div>
                  <div className={clsx(
                    'h-2 w-2 rounded-full',
                    cn.status === 'approved' ? 'bg-emerald-500' : cn.status === 'pending_review' ? 'bg-amber-500' : 'bg-rose-500'
                  )} />
                </div>
              ))}
            </div>
          )}
        </section>
      </>
    )}

       <section className={sectionClass}>
        <h2 className="text-sm font-semibold text-ink mb-4">Integrations</h2>
        <div className="space-y-4">
          <IntegrationRow
            name="Slack"
            connected={!!org?.slack_workspace_id}
            connectUrl={api.getSlackInstallUrl()}
          />
          <div className="border-t border-warm-border/30 pt-3">
            <IntegrationRow
              name="Microsoft Teams"
              connected={!!org?.teams_tenant_id}
              connectUrl={`${BASE_URL}/api/auth/teams/install`}
            />
            {hasTeams && (
              <p className="text-[11px] text-ink-muted mt-1 mb-1 leading-relaxed">
                For nudges in Teams chat, each person should open the <strong className="text-ink font-medium">Duva</strong> bot and send a message once so we can reach them.
              </p>
            )}
            {isAdmin && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowTeamsManifest(!showTeamsManifest)}
                  className="text-[10px] uppercase font-bold text-brand-teal hover:underline tracking-tight"
                >
                  {showTeamsManifest ? 'Hide Manifest Helper' : 'Microsoft Teams Manifest Helper'}
                </button>
                
                {showTeamsManifest && (
                  <div className="mt-3 p-4 bg-warm-inset rounded-xl border border-warm-border text-[11px] text-ink-muted leading-relaxed">
                    <p className="mb-2">
                      To install Duva for your organization, an IT Admin must upload a custom app package in the <strong>Teams Admin Center</strong>.
                    </p>
                    <ol className="list-decimal pl-4 space-y-1 mb-4">
                      <li>In Azure → your Bot, set the messaging endpoint to <code className="text-ink">{`${BASE_URL}/api/teams/webhook`}</code>.</li>
                      <li>Copy the JSON below (or set <code className="text-ink">VITE_TEAMS_APP_ID</code> in the web build for production).</li>
                      <li>Save it as <code>manifest.json</code>, zip with your icons, upload in Teams Admin Center → Manage apps → Upload.</li>
                    </ol>
                    <p className="text-[10px] text-ink-faint mb-2">
                      <code className="text-ink">validDomains</code> includes this site and your API host (<code className="text-ink">{apiHostnameForManifest()}</code>).
                      Add another host (e.g. <code className="text-ink">api.getduva.com</code>) in Azure / manifest if you use a custom API domain.
                    </p>
                    <div className="relative group">
                      <pre className="bg-white p-3 rounded border border-warm-border font-mono overflow-x-auto max-h-48 text-[10px] leading-relaxed">
                        {teamsManifestJson}
                      </pre>
                      <button
                        type="button"
                        onClick={() => {
                          void navigator.clipboard.writeText(teamsManifestJson)
                          toast.success('Manifest JSON copied')
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-warm-card border border-warm-border rounded-lg hover:bg-warm-inset"
                        title="Copy manifest JSON"
                      >
                        <Clipboard size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {isAdmin && (
        <section className={sectionClass}>
          <button
            type="button"
            onClick={() => setSsoOpen(o => !o)}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <div className="flex items-center gap-3">
              <KeyRound size={16} className="text-brand-teal" />
              <div>
                <p className="text-sm font-semibold text-ink">Single Sign-On (SSO)</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  {ssoStatus?.configured
                    ? `Configured — ${providerLabels[ssoStatus.provider ?? ''] ?? ssoStatus.provider} · ${ssoStatus.domain}`
                    : 'Not configured — Enterprise feature'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {ssoStatus?.configured && (
                <span className="text-xs bg-[#eef5ec] text-emerald-800 border border-[#B8C7B1] px-2.5 py-1 rounded-full font-medium">
                  ✓ Active
                </span>
              )}
              {ssoOpen
                ? <ChevronUp size={15} className="text-ink-soft" />
                : <ChevronDown size={15} className="text-ink-soft" />}
            </div>
          </button>

          {ssoOpen && (
            <div className="mt-5 pt-5 border-t border-warm-border space-y-4">
              <p className="text-xs text-ink-muted leading-relaxed">
                Once configured, employees can sign in via their corporate IdP instead of Slack or Microsoft. The SSO URL to share with your IT team will be{' '}
                <code className="text-brand-teal bg-warm-inset px-1.5 py-0.5 rounded text-xs border border-warm-border">
                  {`${BASE_URL}/api/sso/login?domain=<your-domain>`}
                </code>
              </p>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">Identity Provider</label>
                <select
                  value={ssoForm.provider}
                  onChange={e => setSsoForm(f => ({ ...f, provider: e.target.value }))}
                  className={inputClass}
                >
                  <option value="okta">Okta</option>
                  <option value="azure">Microsoft Entra ID (Azure AD)</option>
                  <option value="google">Google Workspace</option>
                  <option value="generic">Generic OIDC</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">
                  Corporate email domain
                  <span className="text-ink-faint ml-1">(e.g. acme.com)</span>
                </label>
                <input
                  type="text"
                  placeholder="acme.com"
                  value={ssoForm.domain}
                  onChange={e => setSsoForm(f => ({ ...f, domain: e.target.value }))}
                  className={`${inputClass} font-mono`}
                />
                <p className="text-xs text-ink-muted mt-1">Users with this email domain will see &quot;Sign in with SSO&quot; on the login page.</p>
              </div>

              <div className="p-4 bg-warm-inset rounded-xl border border-warm-border space-y-3">
                <p className="text-xs font-semibold text-ink">Redirect URI for your IdP</p>
                <p className="text-[11px] text-ink-muted leading-relaxed">
                  Register this URL in your Okta/Azure/IdP application settings as the callback or sign-in redirect:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded border border-warm-border text-xs text-brand-teal font-mono truncate">
                    {`${BASE_URL}/api/sso/callback`}
                  </code>
                </div>
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">
                  {ssoForm.provider === 'okta' ? 'Okta Issuer URL' :
                   ssoForm.provider === 'azure' ? 'Azure Tenant ID / Metadata URL' :
                   ssoForm.provider === 'google' ? 'Google OIDC Issuer' : 'OIDC Issuer URL'}
                </label>
                <input
                  type="url"
                  placeholder={
                    ssoForm.provider === 'okta' ? 'https://yourorg.okta.com/oauth2/default' :
                    ssoForm.provider === 'azure' ? 'https://login.microsoftonline.com/{tenant-id}/v2.0' :
                    ssoForm.provider === 'google' ? 'https://accounts.google.com' :
                    'https://your-idp.example.com'
                  }
                  value={ssoForm.issuer_url}
                  onChange={e => setSsoForm(f => ({ ...f, issuer_url: e.target.value }))}
                  className={`${inputClass} font-mono`}
                />
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">Client ID</label>
                <input
                  type="text"
                  placeholder="0oa1b2c3d4e5..."
                  value={ssoForm.client_id}
                  onChange={e => setSsoForm(f => ({ ...f, client_id: e.target.value }))}
                  className={`${inputClass} font-mono`}
                />
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">Client Secret</label>
                <input
                  type="password"
                  placeholder="••••••••••••••••"
                  value={ssoForm.client_secret}
                  onChange={e => setSsoForm(f => ({ ...f, client_secret: e.target.value }))}
                  className={inputClass}
                />
                <p className="text-xs text-ink-muted mt-1">Stored encrypted at rest in Cloudflare KV. Never logged.</p>
              </div>

              {ssoError && (
                <div className="flex items-center gap-2 text-red-800 bg-[#fdf2f2] border border-[#e8cfcf] rounded-xl px-4 py-3">
                  <AlertCircle size={14} className="shrink-0" />
                  <p className="text-xs">{ssoError}</p>
                </div>
              )}

              <button
                type="button"
                onClick={handleSsoSave}
                disabled={ssoSaving || !ssoForm.issuer_url || !ssoForm.client_id || !ssoForm.client_secret || !ssoForm.domain}
                className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal" style={{background: "#2A7A8A"}}
              >
                {ssoSaved
                  ? <><CheckCircle2 size={14} /> SSO configured!</>
                  : ssoSaving
                  ? <><Save size={14} /> Saving…</>
                  : <><Save size={14} /> Save SSO configuration</>
                }
              </button>
            </div>
          )}
        </section>
      )}

      <section className={sectionClass}>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={16} className="text-brand-teal" />
          <h2 className="text-sm font-semibold text-ink">Security & Compliance</h2>
        </div>
        <div className="space-y-2 text-xs text-ink-muted">
          <p>✓ All data encrypted in transit (TLS) and at rest (D1)</p>
          <p>✓ No passwords stored — identity delegated to Slack/Teams/SSO</p>
          <p>✓ GDPR: right to erasure available below</p>
          <p>✓ Audit log: every mutation logged with actor + timestamp</p>
          <a
            href="/security"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand-teal hover:text-brand-teal-hover mt-2 transition-colors font-medium"
          >
            <ExternalLink size={11} /> View security whitepaper
          </a>
        </div>
      </section>

      <section className={sectionClass}>
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-brand-teal" />
          <h2 className="text-sm font-semibold text-ink">Account</h2>
        </div>
        <div className="text-xs text-ink-muted space-y-1 mb-5">
          <p>Name: <span className="text-ink">{user?.name}</span></p>
          <p>Email: <span className="text-ink">{user?.email}</span></p>
          <p>Role: <span className="text-ink capitalize">{user?.role}</span></p>
        </div>
        <div className="border-t border-warm-border pt-5">
          <label className="block text-xs text-ink-soft mb-1.5">When to send nudges (your local time)</label>
          <p className="text-xs text-ink-muted mb-4 leading-relaxed">
            We target a morning send window in <strong className="text-ink">your</strong> timezone. Choose Cloudflare’s
            guess from your current connection, the workspace default on the server, or type an IANA zone if you need
            something different (e.g. VPN or working across regions).
          </p>

          {meTimezone.trim() ? (
            <p className="text-xs mb-3 text-ink">
              <span className="text-ink-muted">Saved as:</span>{' '}
              <span className="font-medium">{formatTimezoneLabel(meTimezone)}</span>
            </p>
          ) : (
            <p className="text-xs mb-3 text-ink-muted">Currently using workspace default (server).</p>
          )}

          <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={() => void handleUseConnectionTimezone()}
              disabled={tzSaving || !detectedTimezone}
              className="flex items-center justify-center gap-2 text-white font-medium text-sm px-5 py-2.5 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal"
              style={{ background: '#2A7A8A' }}
              title={detectedTimezone ? formatTimezoneLabel(detectedTimezone) : undefined}
            >
              Use my location (Cloudflare)
            </button>
            <button
              type="button"
              onClick={() => void handleWorkspaceDefaultTimezone()}
              disabled={tzSaving}
              className="flex items-center justify-center gap-2 font-medium text-sm px-5 py-2.5 rounded-full border border-warm-border bg-warm-inset text-ink hover:bg-warm-card transition-colors disabled:opacity-50"
            >
              Use workspace default
            </button>
          </div>

          {detectedTimezone ? (
            <p className="text-[11px] text-ink-faint mb-3">
              Detected for this session: <span className="font-mono text-ink-muted">{detectedTimezone}</span>
              {' · '}
              {formatTimezoneLabel(detectedTimezone)}
            </p>
          ) : (
            <p className="text-[11px] text-ink-faint mb-3">
              Connection timezone isn’t available here (common in local dev). Use manual IANA or deploy traffic through
              Cloudflare.
            </p>
          )}

          <button
            type="button"
            onClick={() => setManualIanaOpen((o) => !o)}
            className="text-xs font-medium text-brand-teal hover:underline underline-offset-2 mb-2"
          >
            {manualIanaOpen ? 'Hide manual IANA entry' : 'Enter IANA manually instead'}
          </button>

          {manualIanaOpen && (
            <div className="mt-2 space-y-2 rounded-xl border border-warm-border bg-warm-inset/50 p-4">
              <input
                type="text"
                value={meTimezone}
                onChange={(e) => setMeTimezone(e.target.value)}
                className={`${inputClass} font-mono text-xs`}
                placeholder="e.g. America/Los_Angeles"
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-xs text-ink-faint">
                <a
                  href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-teal hover:underline"
                >
                  Browse IANA time zones
                </a>
              </p>
              <button
                type="button"
                onClick={() => void handleSaveTimezone()}
                disabled={tzSaving}
                className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal"
                style={{ background: '#2A7A8A' }}
              >
                <Save size={14} />
                {tzSaved ? 'Saved!' : tzSaving ? 'Saving…' : 'Save IANA'}
              </button>
            </div>
          )}

          {tzError && (
            <p className="text-xs text-red-800 mt-3 bg-[#fdf2f2] border border-[#e8cfcf] rounded-lg px-3 py-2">{tzError}</p>
          )}
        </div>
      </section>

      <section className="bg-[#fdf2f2] border border-[#e8cfcf] rounded-2xl p-6">
        <h2 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
          <Trash2 size={14} /> Danger Zone
        </h2>
        <p className="text-xs text-ink-muted mb-4">
          Erase your personal data (GDPR right to erasure). This removes your name, email, and delivery history. Organization data is retained.
        </p>
        {confirmErase && (
          <p className="text-xs text-red-800 mb-3 font-medium">⚠️ Are you sure? This cannot be undone.</p>
        )}
        <button
          type="button"
          onClick={handleErase}
          disabled={erasing}
          className="flex items-center gap-2 bg-white hover:bg-[#fce8e8] border border-[#e8cfcf] text-red-800 font-medium text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          <Trash2 size={13} />
          {erasing ? 'Erasing…' : confirmErase ? 'Confirm erase my data' : 'Erase my personal data'}
        </button>
      </section>
    </div>
  )
}

function IntegrationRow({ name, connected, connectUrl }: {
  name: string; connected: boolean; connectUrl: string
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm text-ink font-medium">{name}</p>
        <p className="text-xs text-ink-muted">{connected ? 'Connected' : 'Not connected'}</p>
      </div>
      {connected ? (
        <span className="text-xs bg-[#eef5ec] text-emerald-800 border border-[#B8C7B1] px-2.5 py-1 rounded-full font-medium">
          ✓ Connected
        </span>
      ) : (
        <a
          href={connectUrl}
          className="text-xs text-white font-medium px-4 py-1.5 rounded-full transition-colors shadow-glow-teal"
          style={{ background: '#2A7A8A' }}
        >
          Connect
        </a>
      )}
    </div>
  )
}
